import type { TrackingEventCallback } from '@chilipiper/web-data-external'
import { logger } from './logger'

/**
 * Lets the host page (or our own embeds) react to what the tracking script sends: a customer can
 * pipe our events into their analytics, and the chat embed can react in-session to a form fill or a
 * page view. Listeners are notified from the jitsu wrapper after an event actually went out, so a
 * paused wrapper (consent denied, customer disabled) never notifies anyone.
 */

const ALL_EVENTS = '*'

const listeners = new Map<string, Set<TrackingEventCallback>>()

export const onTrackingEvent = (event: string, callback: TrackingEventCallback): (() => void) => {
  let callbacks = listeners.get(event)
  if (!callbacks) {
    callbacks = new Set()
    listeners.set(event, callbacks)
  }
  callbacks.add(callback)
  return () => offTrackingEvent(event, callback)
}

export const offTrackingEvent = (event: string, callback: TrackingEventCallback): void => {
  const callbacks = listeners.get(event)
  if (!callbacks) return
  callbacks.delete(callback)
  if (callbacks.size === 0) {
    listeners.delete(event)
  }
}

/**
 * Notifies the event's listeners and the wildcard listeners. A throwing listener never breaks the
 * script.
 */
export const emitTrackingEvent = (event: string, payload: unknown): void => {
  const targets = [...(listeners.get(event) ?? []), ...(listeners.get(ALL_EVENTS) ?? [])]
  for (const callback of targets) {
    try {
      callback(event, payload)
    } catch (error) {
      logger.error(`A listener for "${event}" threw`, error)
    }
  }
}
